import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useMediaQuery } from 'react-responsive';

import CustomAxios from '../utils/CustomAxios';
import Header from '../components/header';
import Sidebar from '../components/sidebar';
import MuiButton from '../elements/Mui/mui_button';
import { blobErrorMessage, deliverPdf, filenameFromResponse } from '../utils/downloadPdf';

const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export const MemberDividendWarrant = () => {
  const isMobile = useMediaQuery({ maxWidth: 600 });
  const memberid = parseInt(localStorage.getItem('memberid'), 10);

  const [warrants, setWarrants] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    CustomAxios.post('oasis/member_dividend_warrant/', { memberid })
      .then((res) => setWarrants(res.data.warrants || []))
      .catch((err) => {
        setWarrants([]);
        setError(err?.response?.data?.message || 'Could not load your dividend warrants. Please try again.');
      });
  }, [memberid]);

  const download = async (warrant) => {
    setError('');
    setDownloading(warrant.divid);
    try {
      const res = await CustomAxios.post('oasis/member_dividend_warrant/download/', { memberid, divid: warrant.divid }, { responseType: 'blob' });
      deliverPdf(res.data, filenameFromResponse(res, 'DividendWarrant.pdf'), { openInTab: false });
    } catch (err) {
      setError(await blobErrorMessage(err, 'Could not download your dividend warrant. Please try again.'));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Header title="Dividend Warrant" />
      <Box sx={{ display: { xs: 'block', md: 'flex' } }}>
        {!isMobile && <Sidebar />}
        <Box sx={{ p: 2, width: '100%', maxWidth: '760px' }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>My Dividend Warrants</Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {warrants === null && <CircularProgress aria-label="Loading dividend warrants" />}

          {warrants?.length === 0 && !error && (
            <Box sx={{ border: '1px dashed #CBD5E1', borderRadius: '6px', padding: '24px', color: '#475569', textAlign: 'center' }}>
              No dividend warrant is available yet. It will appear here once the co-operative releases it.
            </Box>
          )}

          <Stack sx={{ gap: '12px' }}>
            {warrants?.map((warrant) => (
              <Stack
                key={warrant.divid}
                direction={isMobile ? 'column' : 'row'}
                sx={{ gap: '12px', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '16px', background: '#FFFFFF' }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>{warrant.fy_label}</Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '14px' }}>
                    Net RM {formatAmount(warrant.net)}{warrant.payment_date ? ` · paid ${warrant.payment_date}` : ''}
                  </Typography>
                </Box>
                <MuiButton
                  variant="contained"
                  value={downloading === warrant.divid ? <><CircularProgress size={18} color="inherit" /> Downloading…</> : 'Download PDF'}
                  disabled={downloading !== null}
                  onClick={() => download(warrant)}
                  btnsx={{ width: isMobile ? '100%' : '180px' }}
                />
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
